import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { Button, Alert } from '@mui/material';
import HeaderPublic from '../../components/HeaderPublic';
import Footer from '../../components/Footer';
import { api } from '../../services/apiService';
import { getUserDetails, requestToken } from '../../services/teamService';
import { updateUserProfile } from '../../services/userService';

const TeamsRegister = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [designation, setDesignation] = useState('');
  const [organization, setOrganization] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    async function fetchData() {
      const encodedEmail = window.location.pathname.split('/')[3];
      
      try {
        const userDetails = await getUserDetails(encodedEmail);
        console.log('Full userDetails response:', userDetails);
        
        if (userDetails.status === 'success' && userDetails.data) {
          console.log('userDetails.data:', userDetails.data);
          const { firstName, lastName, email, phone, designation, company, id, userId, _id } = userDetails.data;
          
          // Set the form data
          setFirstName(firstName || '');
          setLastName(lastName || '');
          setEmail(email || '');
          setPhone(phone || '');
          setDesignation(designation || '');
          setOrganization(company || '');
          
          // Try different possible field names for user ID
          const userIdentifier = id || userId || _id || userDetails.data.id || userDetails.data.userId || userDetails.data._id;
          console.log('User identifier found:', userIdentifier);
          setUserId(userIdentifier);
          
          // Check if all required data is present (firstName, lastName, email, phone, company)
          if (firstName && lastName && email && phone && company) {
            try {
              // Call requestToken with encodedEmail
              const tokenResponse = await requestToken(encodedEmail);
              
              if (tokenResponse && tokenResponse.data) {
                // Set token data in localStorage
                localStorage.setItem('token', tokenResponse.data.token || tokenResponse.data);
                localStorage.setItem('userData', JSON.stringify({
                  firstName,
                  lastName,
                  email,
                  phone,
                  designation,
                  company
                }));
                
                // Redirect to dashboard
                window.location.href = '/dashboard';
              }
            } catch (error) {
              console.error('Error requesting token:', error);
              setError('Failed to authenticate. Please try again.');
            }
          } else {
            // If required data is missing, show message asking user to fill the form
            setError('Please complete all required fields (First Name, Last Name, Email, Phone, Organization) to proceed.');
          }
        } else {
          // User doesn't exist yet, show form for them to fill out
          console.log('User not found, showing form for registration');
          setError('Please complete all required fields to create your account.');
        }
      } catch (error) {
        console.error('Error fetching user details:', error);
        // User doesn't exist yet, show form for them to fill out
        setError('Please complete all required fields to create your account.');
      }
    }
    fetchData();
  }, []);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }
    
    try {
      setSubmitting(true);
      const profileData = {
        firstName,
        lastName,
        email,
        phone,
        designation,
        company: organization,
        source: 'public',
      };
      
      let response;
      
      if (userId) {
        // User exists, update their profile
        console.log('Updating existing user with ID:', userId);
        response = await updateUserProfile(userId, profileData);
        setSuccess('Profile updated successfully!');
      } else {
        // User doesn't exist, create new user
        console.log('Creating new user');
        response = await api.post('/users/register', profileData);
        setSuccess('Account created successfully!');
      }
      
      if (response?.data) {
        // After successful create/update, try to get token and redirect
        try {
          const encodedEmail = window.location.pathname.split('/')[3];
          const tokenResponse = await requestToken(encodedEmail);
          
          if (tokenResponse && tokenResponse.data) {
            // Set token data in localStorage
            localStorage.setItem('token', tokenResponse.data.token || tokenResponse.data);
            localStorage.setItem('userData', JSON.stringify({
              firstName,
              lastName,
              email,
              phone,
              designation,
              company: organization
            }));
            
            // Redirect to dashboard
            window.location.href = '/dashboard';
          }
        } catch (tokenError) {
          console.error('Error requesting token after create/update:', tokenError);
          setError('Account created/updated but failed to authenticate. Please try logging in.');
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create/update account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <HeaderPublic />

      <Container maxWidth="md" sx={{ flex: 1, py: 6 }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0a1128', mb: 1 }}>Create your account</Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>Fill in your details to get started.</Typography>
        </Box>

        <Box sx={{
          bgcolor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          boxShadow: 1,
          p: 3,
        }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label htmlFor="firstName" style={{ fontWeight: 600 }}>First Name</label>
                <input id="firstName" name="firstName" type="text" value={firstName} style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} onChange={(e) => setFirstName(e.target.value)} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <label htmlFor="lastName" style={{ fontWeight: 600 }}>Last Name</label>
                <input id="lastName" name="lastName" type="text" value={lastName} style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} onChange={(e) => setLastName(e.target.value)} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1 }}>
                <label htmlFor="email" style={{ fontWeight: 600 }}>Email</label>
                <input id="email" name="email" type="email" value={email} readOnly style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <label htmlFor="phone" style={{ fontWeight: 600 }}>Phone</label>
                <input id="phone" name="phone" type="text" value={phone} style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} onChange={(e) => setPhone(e.target.value)} />
              </Box>
            </Box>

            <Box sx={{ mb: 2 }}>
              <label htmlFor="designation" style={{ fontWeight: 600 }}>Designation</label>
              <input id="designation" name="designation" type="text" value={designation} style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} onChange={(e) => setDesignation(e.target.value)} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <label htmlFor="organization" style={{ fontWeight: 600 }}>Organization</label>
              <input id="organization" name="organization" type="text" value={organization} style={{width: '100%', padding: 8, borderRadius: 4, border: '1px solid #ccc'}} onChange={(e) => setOrganization(e.target.value)} />
            </Box>

            {error && (<Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>)}
            {success && (<Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>)}

            <Button type="submit" disabled={submitting} sx={{ mt: 1, bgcolor: '#1BE885', color: '#212121', fontWeight: 700, border: 0, borderRadius: 1, px: 3, py: 1.2, '&:hover': { bgcolor: '#17d077' } }}>
              {submitting ? 'Processing...' : 'Submit'}
            </Button>
          </form>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default TeamsRegister;

